import type { Coordinates } from '@freedom/shared';

export interface MapStyleDefinition {
  version: 8;
  glyphs?: string;
  sources: Record<string, unknown>;
  layers: Array<Record<string, unknown>>;
}

export interface MapProvider {
  readonly id: string;
  readonly title: string;
  createStyle(): MapStyleDefinition;
}

export interface GeocodingResult {
  id: string;
  displayName: string;
  coordinates: Coordinates;
  boundingBox: [number, number, number, number] | null;
  category: string | null;
  source: string;
}

export interface GeocodingProvider {
  readonly name: string;
  search(query: string, limit: number): Promise<GeocodingResult[]>;
}

export class OpenStreetMapProvider implements MapProvider {
  public readonly id = 'openstreetmap';
  public readonly title = 'OpenStreetMap';

  public constructor(
    private readonly tileUrl = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    private readonly attribution = '© OpenStreetMap contributors',
  ) {}

  public createStyle(): MapStyleDefinition {
    return {
      version: 8,
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      sources: {
        osm: { type: 'raster', tiles: [this.tileUrl], tileSize: 256, attribution: this.attribution },
      },
      layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
    };
  }
}

const EARTH_RADIUS_M = 6_371_008.8;
const radians = (degrees: number): number => (degrees * Math.PI) / 180;
const degrees = (value: number): number => (value * 180) / Math.PI;

export function haversineDistanceM(a: Coordinates, b: Coordinates): number {
  const deltaLat = radians(b.lat - a.lat);
  const deltaLon = radians(b.lon - a.lon);
  const latA = radians(a.lat);
  const latB = radians(b.lat);
  const h = Math.sin(deltaLat / 2) ** 2 + Math.cos(latA) * Math.cos(latB) * Math.sin(deltaLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

export function initialBearingDeg(a: Coordinates, b: Coordinates): number {
  const latA = radians(a.lat);
  const latB = radians(b.lat);
  const deltaLon = radians(b.lon - a.lon);
  const y = Math.sin(deltaLon) * Math.cos(latB);
  const x = Math.cos(latA) * Math.sin(latB) - Math.sin(latA) * Math.cos(latB) * Math.cos(deltaLon);
  return (degrees(Math.atan2(y, x)) + 360) % 360;
}

export function destinationPoint(origin: Coordinates, bearingDeg: number, distanceM: number): Coordinates {
  if (distanceM < 0) throw new RangeError('Distance cannot be negative');
  const angularDistance = distanceM / EARTH_RADIUS_M;
  const bearing = radians(bearingDeg);
  const latitude = radians(origin.lat);
  const longitude = radians(origin.lon);
  const targetLatitude = Math.asin(Math.sin(latitude) * Math.cos(angularDistance) + Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing));
  const targetLongitude = longitude + Math.atan2(Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude), Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(targetLatitude));
  return { lat: degrees(targetLatitude), lon: ((degrees(targetLongitude) + 540) % 360) - 180 };
}

export function coverageBoundary(origin: Coordinates, radiusM: number, azimuthDeg: number, beamwidthDeg: number): Coordinates[] {
  if (radiusM <= 0 || beamwidthDeg <= 0 || beamwidthDeg > 360) throw new RangeError('Invalid coverage boundary parameters');
  const fullCircle = beamwidthDeg >= 359.9;
  const steps = Math.max(24, Math.ceil(beamwidthDeg / 3));
  const startBearing = fullCircle ? 0 : azimuthDeg - beamwidthDeg / 2;
  const arc = Array.from({ length: steps + 1 }, (_, index) => destinationPoint(origin, startBearing + beamwidthDeg * index / steps, radiusM));
  return fullCircle ? [...arc, arc[0] ?? origin] : [origin, ...arc, origin];
}

export function interpolateGreatCircle(a: Coordinates, b: Coordinates, fraction: number): Coordinates {
  if (fraction < 0 || fraction > 1) throw new RangeError('fraction must be between 0 and 1');
  const distance = haversineDistanceM(a, b) / EARTH_RADIUS_M;
  if (distance === 0) return { ...a };
  const coefficientA = Math.sin((1 - fraction) * distance) / Math.sin(distance);
  const coefficientB = Math.sin(fraction * distance) / Math.sin(distance);
  const latA = radians(a.lat);
  const lonA = radians(a.lon);
  const latB = radians(b.lat);
  const lonB = radians(b.lon);
  const x = coefficientA * Math.cos(latA) * Math.cos(lonA) + coefficientB * Math.cos(latB) * Math.cos(lonB);
  const y = coefficientA * Math.cos(latA) * Math.sin(lonA) + coefficientB * Math.cos(latB) * Math.sin(lonB);
  const z = coefficientA * Math.sin(latA) + coefficientB * Math.sin(latB);
  return { lat: degrees(Math.atan2(z, Math.sqrt(x * x + y * y))), lon: degrees(Math.atan2(y, x)) };
}

export function pointInPolygon(point: Coordinates, polygon: Coordinates[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let current = 0, previous = polygon.length - 1; current < polygon.length; previous = current, current += 1) {
    const a = polygon[current];
    const b = polygon[previous];
    if (!a || !b) continue;
    const intersects = ((a.lat > point.lat) !== (b.lat > point.lat)) && point.lon < ((b.lon - a.lon) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lon;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function pathBoundingBox(start: Coordinates, end: Coordinates, corridorM = 100): [number, number, number, number] {
  const meanLatitude = (start.lat + end.lat) / 2;
  const latPadding = corridorM / 111_320;
  const lonPadding = corridorM / (111_320 * Math.max(0.1, Math.cos(radians(meanLatitude))));
  return [Math.min(start.lat, end.lat) - latPadding, Math.min(start.lon, end.lon) - lonPadding, Math.max(start.lat, end.lat) + latPadding, Math.max(start.lon, end.lon) + lonPadding];
}
